import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, updateDoc, getDoc, setDoc } from "firebase/firestore";
import { db } from "lib/firebase";
import { useCallback, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { User } from "types/user";
import { useLanguage } from "contexts/LanguageContext";

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { t } = useLanguage();

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'users'), orderBy('displayName', 'asc'))
      const querySnapshot = await getDocs(q);
      const loadedUsers = querySnapshot.docs.map(doc => {
        const userData = doc.data();
        return {
          ...userData,
        id: doc.id,
          createdAt: userData.createdAt?.toDate(),
          updatedAt: userData.updatedAt?.toDate(),
          lastLoginAt: userData.lastLoginAt?.toDate()
        };
      }) as User[];
      
      setUsers(loadedUsers);
      
      // Create a map for quick lookups
      const userMap = new Map<string, User>();
      loadedUsers.forEach(user => {
        userMap.set(user.id, user);
      });
      setUsersMap(userMap);
    } catch (error) {
      toast.error(t('systemAdmin.messages.user.failedToLoad'));
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Get user by ID from cache or fetch from Firebase
  const getUserById = useCallback(async (userId: string): Promise<User | null> => {
    // First check in cache
    if (usersMap.has(userId)) {
      return usersMap.get(userId)!;
    }

    // If not in cache, fetch from Firebase
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        const user: User = {
          ...userData,
          id: userDoc.id,
          createdAt: userData.createdAt,
          updatedAt: userData.updatedAt,
          lastLoginAt: userData.lastLoginAt
        };
        
        // Add to cache
        setUsersMap(prev => new Map(prev.set(userId, user)));
        
        return user;
      }
    } catch (error) {
      console.error('Error fetching user by ID:', error);
    }
    
    return null;
  }, [usersMap]);

  // Get user display name - utility function
  const getUserDisplayName = useCallback((userId: string): string => {
    const user = usersMap.get(userId);
    if (user) {
      return user.displayName || user.email || 'Unknown User';
    }
    
    // Return a truncated version if not found
    return userId ? `User-${userId.substring(0, 8)}...` : 'Unknown User';
  }, [usersMap]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const createUser = async (user: User) => {
    try {
      const usersRef = collection(db, 'users');
      const docRef = await addDoc(usersRef, {
        ...user,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      const newUser = {
        ...user,
        id: docRef.id
      };
      // Update local state immediately
      setUsers(prevUsers => [...prevUsers, newUser]);
      toast.success(t('systemAdmin.messages.user.createdSuccess'));
      return newUser;
    } catch (error) {
      toast.error(t('systemAdmin.messages.user.createdError'));
      throw error;
    }
  };

  const updateUser = async (id: string, data: Partial<User>) => {
    try {
      const userRef = doc(db, 'users', id);
      await updateDoc(userRef, data);

      // Update local state immediately
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === id
            ? { ...user, ...data }
            : user
        )
      );

      toast.success(t('systemAdmin.messages.user.updatedSuccess'));
      return { id, ...data };
    } catch (error) {
      toast.error(t('systemAdmin.messages.user.updatedError'));
      throw error;
    }
  };

  const deleteUser = async (id: string) => {
    try {
      const userRef = doc(db, 'users', id);
      await deleteDoc(userRef);

      // Update local state immediately
      setUsers(prevUsers => prevUsers.filter(user => user.id !== id));
      toast.success(t('systemAdmin.messages.user.deletedSuccess'));
      return true;
    } catch (error) {
      toast.error(t('systemAdmin.messages.user.deletedError'));
      throw error;
    }
  };

  const unlockUser = async (user: User) => {
    try {
      const emailKey = (user.email || '').toLowerCase();
      if (emailKey) {
        const ref = doc(db, 'loginAttempts', emailKey);
        await setDoc(ref, { email: emailKey, attempts: 0, locked: false, lastAttemptAt: new Date() }, { merge: true });
      }
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === user.id ? { ...u, locked: false } : u
        )
      );
      toast.success(t('systemAdmin.messages.user.unlockedSuccess') || 'Felhasználó feloldva');
      return true;
    } catch (error) {
      toast.error(t('systemAdmin.messages.user.unlockedError') || 'Hiba a feloldás során');
      throw error;
    }
  };

  return { users, usersMap, loading, error, createUser, updateUser, deleteUser, unlockUser, refresh: loadUsers, getUserById, getUserDisplayName };
};

